import React from "react";
import moment from "moment";
import { AdministratorEvent } from "../../../models/User";

import useStyles from "./styles";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { Box, Grid, Paper, Typography, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Empty } from "../misc/Empty";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import { Event } from "@mui/icons-material";
import { prepareExportData } from "../../../utils";
import { useI18n } from "../../../hooks/useI18n";

export interface AdminEventProps {
  loading: boolean;
  administratorEvents: [];
}

const AdministratorEventsCollection = ({
  loading,
  administratorEvents
}: AdminEventProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const rowsPerPage = mobile ? 5 : 10;
  const { t } = useI18n();

  const exportDataFormat = [{ type: "datetime", index: 0 }];

  return (
    <div style={{ position: "relative" }}>
      <Box
        display={"flex"}
        alignItems={"center"}
        position={mobile ? "relative" : "absolute"}
        zIndex={10}
      >
        <Event sx={{ fontSize: "3rem", color: theme.palette.success.dark }} />
        <Box pl={2}>
          <Typography variant={"h6"} color={"textSecondary"}>
            Admin Events
          </Typography>
        </Box>
      </Box>
      <MaterialTable
        title={t("common.messages.adminEvents")}
        isLoading={loading}
        columns={[
          {
            title: "Timestamp",
            field: "timeStamp",
            cellStyle: { padding: "8px 20px 8px 8px" },
            render: (row: any) =>
              moment(row.timeStamp).format("YYYY-MM-DD HH:mm")
          },
          {
            title: "TenantId",
            field: "tenantId",
            hidden: true,
            cellStyle: { padding: "8px 20px 8px 8px" }
          },
          {
            title: "TargetId",
            field: "targetId",
            hidden: true,
            cellStyle: { padding: "8px 20px 8px 8px" }
          },
          {
            title: "Event",
            field: "event",
            cellStyle: { padding: "8px" },
            render: (row: any) => (
              <>
                <Typography variant={"body2"}>{row.event}</Typography>
                {row.event === AdministratorEvent.UserStatus ? (
                  <Typography variant={"body2"} color={"textSecondary"}>
                    {row.data?.status}
                  </Typography>
                ) : row.event === AdministratorEvent.EmployeeStatus ? (
                  <Typography variant={"body2"} color={"textSecondary"}>
                    {row.data?.employee
                      ? t("common.messages.employed")
                      : t("common.messages.notEmployed")}
                  </Typography>
                ) : row.event === AdministratorEvent.VerifyPurchase ? (
                  <Typography variant={"body2"} color={"textSecondary"}>
                    {row.data?.declined ? "Declined" : "Verified"}
                  </Typography>
                ) : (
                  <></>
                )}
              </>
            )
          },
          {
            title: "Administrator",
            field: "userName",
            cellStyle: { padding: "8px" }
          }
        ]}
        data={administratorEvents ? (administratorEvents as []) : []}
        detailPanel={[
          rowData => ({
            disabled: !rowData.data,
            render: () => {
              if (rowData.data) {
                return (
                  <Grid container spacing={2} className={classes.historyPanel}>
                    {Object.keys(rowData.data).map((d: any) => {
                      return (
                        <Grid item xs={12} lg={3}>
                          <Typography variant="body2">
                            <strong>
                              {d.charAt(0).toUpperCase() + d.slice(1)}
                            </strong>
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            gutterBottom
                          >
                            {Array.isArray(rowData.data[d])
                              ? rowData.data[d].join(", ")
                              : rowData.data[d].toString()}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              } else return "";
            }
          })
        ]}
        components={{
          Container: props => <Paper {...props} elevation={0} />
        }}
        options={{
          search: true,
          showTitle: false,
          pageSize: rowsPerPage,
          pageSizeOptions: [10, 20, 50, 100],
          emptyRowsWhenPaging: true,
          searchFieldAlignment: mobile ? "left" : "right",
          headerStyle: {
            padding: "8px"
          },
          exportMenu: !mobile
            ? [
                {
                  label: t("common.export.exportPdf"),
                  exportFunc: (cols, data) => {
                    const d = prepareExportData(cols, data, exportDataFormat);
                    ExportPdf(d[0], d[1], `AdminEvents`);
                  }
                },
                {
                  label: t("common.export.exportCsv"),
                  exportFunc: (cols, data) => {
                    const d = prepareExportData(cols, data, exportDataFormat);
                    ExportCsv(d[0], d[1], `AdminEvents`);
                  }
                }
              ]
            : [],
          exportAllData: true
        }}
      />
    </div>
  );
};
export default AdministratorEventsCollection;
